import {IGruppenDB, ISchueler_gruppenDB, ISchuelerDB} from "./InterfacesDB";
import Veraportal from "./Veraportal";
import {IPortalseite} from "./Interfaces";
import Utilities from "./Utilities";
import Modal from "./Modal";
import VeraApi from "./VeraApi";
import Statistik from "./Statistik";

/**
 * @interface IImportDaten
 * @description Definiert die Struktur für importierte Schülerdaten aus der Zwischenablage
 */
interface IImportDaten {
    Klarname?: string;
    Geschlecht?: string;
    Stammklasse?: string;
    F?: string;
    S?: string;
    Pseudonym?: string;
}

/**
 * @class Datenimport
 * @description Diese Klasse steuert die Funktionalität der Datenimport-Seite,
 * auf der Lehrkräfte Schülerlisten aus einer Tabellenkalkulation einfügen können,
 * um Schüler und Stammklassen im VERA-Portal anzulegen
 * @extends Veraportal
 * @implements IPortalseite
 */
export default class Datenimport extends Veraportal implements IPortalseite {
    private importDaten: IImportDaten[] = null; // enthält die aus der Zwischenablage normalisierten Daten

    /**
     * @description Wird aufgerufen, sobald die Seite geladen wird. Initialisiert die Oberfläche und die Event-Listener
     * @returns {Promise<boolean>}
     */
    override async onSeiteLoad(): Promise<boolean> {
        const datenimportDom = document.querySelector(`div.datenimport-container`) as HTMLDivElement ?? null;

        if (datenimportDom === null)
            return false;

        const textareaPlaceholder = Utilities.multilineText(`
            Schülerliste hier einfügen ...
                  1. Schülerliste in Tabellenkalkulation (Microsoft Excel, Libreoffice Calc, etc.) öffnen
                  2. ganze Tabelle oder ganzes Tabellenblatt markieren (STRG + A)
                  3. markierten Bereich kopieren (STRG + C)
                  4. in diesem Textfeld einfügen (STRG + V)
                  5. eingefügte Daten grob prüfen
                  6. Button 'Jahrgang importieren ...' betätigen
        `);

        // Oberfläche für den Datenimport rendern
        datenimportDom.innerHTML = `
            <aside id="hinweis-altdaten" class="hinweis icon">
                <p>
                    Daten können nur importiert werden, wenn bisher keine Schüler oder Gruppen im ${this.sessionvariablen.projekt.bezeichnung}-Portal angelegt wurden. Sollten fälschlicherweise bereits Daten hinterlegt sein,
                    können Sie diese mit <i>"Jahrgang zurücksetzen"</i> komplett löschen. Beachten Sie, dass dabei alle bisherigen Eingaben im Portal verloren gehen.
                </p>
                <button type="button" id="schule-zuruecksetzen-btn" class="secondary">
                    <svg role='graphics-symbol'>
                        <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#trash'/>
                    </svg>
                    Jahrgang<br/>zurücksetzen ...
                </button>
            </aside>
            
            <aside id="hinweis-klarnamen" class="hinweis icon">
                <p>
                    Die Klarnamenfunktion muss für den Import von Daten aus dem Schülerverzeichnis aktiviert sein. Klicken Sie auf den Button <i>"Klarnamen entschlüsseln"</i>
                    rechts oben im Hauptmenü, um die <b>Klarnamenfunktion zu aktivieren</b> bzw. bestehende Klarnamen zu entschlüsseln.
                </p>
            </aside>

            <aside id="hinweis-testheftvariante" class="hinweis icon">
                <p>
                    Legen Sie zuerst unter <a href="/schule-verwalten.php">Testheftvariante wählen</a> die schulweite Testheftvariante fest.
                </p>
            </aside>
            
            <div id="datenimport-oberflaeche">
                <b>Tabelle in dieses Textfeld einfügen:</b>
                <div class="textarea">
                    <textarea id="datenimport-paste-area" readonly placeholder="${textareaPlaceholder}"></textarea>
                    <button type="button" id="datenimport-reset-btn" class="secondary small">
                        <svg role='graphics-symbol'>
                            <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#eraser'/>
                        </svg>
                        Textfeld leeren
                    </button>
                </div>
                <div id="datenimport-details" class="details"></div>
                <nav>
                    <button type="button" id="datenimport-importieren-btn" disabled="disabled">
                        <svg role='graphics-symbol'>
                            <use href='/lib/bootstrap-icons/bootstrap-icons/bootstrap-icons.svg#box-arrow-down'/>
                        </svg>
                        Jahrgang importieren ...
                    </button>
                </nav>
                
            </div>
        `;

        await this.initDatenimport();
        document.addEventListener('onTresorOffen', async () => {
            await this.initDatenimport();
        });

        // Event-Listener für das Einfügen von Daten in die Textarea
        document.querySelector(`#datenimport-paste-area`)
            .addEventListener('paste', async (e: ClipboardEvent) => this.reformatClipboardData(e));

        // Event-Listener für das Leeren der Textarea
        document.querySelector(`#datenimport-reset-btn`)
            .addEventListener('click', () => {
                (document.querySelector(`#datenimport-paste-area`) as HTMLTextAreaElement).value = '';
                (document.querySelector(`#datenimport-details`) as HTMLDivElement).innerHTML = '';
                (document.querySelector(`#datenimport-importieren-btn`) as HTMLButtonElement).disabled = true;
            });

        // Event-Listener für den Import-Button
        document.querySelector(`#datenimport-importieren-btn`)
            .addEventListener('click', async () => {
                (document.querySelector(`#datenimport-importieren-btn`) as HTMLButtonElement).disabled = true;
                let uebermittlungResult = await this.datenUebermitteln(this.importDaten);

                if (uebermittlungResult === false) {
                    // Schule wieder komplett zurücksetzen, wenn ein Fehler passiert ist
                    const api = VeraApi.getInstance(this);
                    await api.resetSchule(this.sessionvariablen.schule.schulnummer);
                    await Utilities.delay(1000);
                    window.location.reload();
                }

                let hinweisModal = new Modal(this);
                const vera5Hinweis = `
                    <p>
                        Die schulweit festgelegte Testheftvariante für Deutsch und Mathematik wurde automatisch allen importierten Stammklassen zugewiesen.
                        Prüfen Sie die importierten Daten und legen Sie bei Bedarf anschließend Kurse an.
                    </p>`;
                const standardHinweis = `
                    <p>
                        Sofern Ihre Schule im Kurssystem unterrichtet, legen Sie die fachspezifischen Kurse manuell an und treffen Sie dort die Testheftauswahl. Weisen Sie anschließend alle Schüler ihren Kursen zu.
                        <b>Prüfen Sie in jedem Fall über das Benachrichtigungscenter</b> (Glockensymbol), in welchen Gruppen noch eine <b>Testheftauswahl erforderlich</b> ist und welche weiteren Eingaben
                        für den Abschluss des <i>Druckbedarfes</i> noch getätigt werden müssen.
                    </p>`;
                await hinweisModal.show({
                    title: `Import erfolgreich`,
                    body: `
                        <p>
                            Sie werden jetzt auf die Schülerverwaltung weitergeleitet. Prüfen Sie die Richtigkeit Ihrer importierten Daten und ergänzen Sie fehlende Eigenschaften.
                        </p>
                        ${(this.sessionvariablen.projekt.weiche === 'vera5') ? vera5Hinweis : standardHinweis}`,
                    hasSubmit: false,
                    hasCancel: true,
                    hasBestaetigung: false,
                    width: 700,
                    cancelText: 'OK'
                });

                // Statistik zur Nutzungshäufigkeit der Importfunktion eingefügt
                const stat = Statistik.getInstance();
                stat.pushStat('schuldatenimport', '1');

                await Utilities.delay(1000);
                window.location.href = '/schuelerverwaltung.php';
            });

        return true;
    }

    /**
     * @description Wird aufgerufen, wenn die Seite vollständig gerendert ist
     * @returns {Promise<boolean>}
     */
    override async onSeiteFinished(): Promise<boolean> {
        return true;
    }

    /**
     * @description Wird aufgerufen, bevor die Seite verlassen wird
     * @returns {Promise<boolean>}
     */
    override async onSeiteLeave(): Promise<boolean> {
        return true;
    }

    /**
     * @description Initialisiert den Zustand der Datenimport-Oberfläche.
     * Prüft, ob der Klarnamen-Tresor geöffnet ist und ob bereits Daten vorhanden sind,
     * und passt die Sichtbarkeit der entsprechenden UI-Elemente an
     * @private
     * @returns {Promise<void>}
     */
    private async initDatenimport(): Promise<void> {
        // Hinweis für Klarnamen-Aktivierung
        if (this.tresor.offen === false)
            document.querySelector(`#hinweis-klarnamen`)?.classList.remove('hidden');
        else
            document.querySelector(`#hinweis-klarnamen`)?.classList.add('hidden');

        // Hinweis bei bereits existierenden Daten
        if (this.schuelerliste.length !== 0 || this.gruppenliste.length !== 0)
            document.querySelector(`#hinweis-altdaten`)?.classList.remove('hidden');
        else
            document.querySelector(`#hinweis-altdaten`)?.classList.add('hidden');

        // Import-Oberfläche aktivieren/deaktivieren
        if (
            this.tresor.offen === true &&
            this.schuelerliste.length === 0 &&
            this.gruppenliste.length === 0 &&
            (this.sessionvariablen.projekt.weiche !== 'vera5' || this.istVera5TestheftvarianteFestgelegt)
        )
            document.querySelector(`#datenimport-oberflaeche`)?.classList.remove('disabled');
        else
            document.querySelector(`#datenimport-oberflaeche`)?.classList.add('disabled');

        if (this.sessionvariablen.projekt.weiche === 'vera5' && this.istVera5TestheftvarianteFestgelegt === false)
            document.querySelector(`#hinweis-testheftvariante`)?.classList.remove('hidden');
        else
            document.querySelector(`#hinweis-testheftvariante`)?.classList.add('hidden');
    }

    /**
     * @description Formatiert ein zweidimensionales Array oder ein Array von Objekten als formatierte Tabelle in einem String.
     * Die Spaltenbreiten werden automatisch an den längsten Inhalt angepasst
     * @param {string[][] | object[]} data - Das zu formatierende Array
     * @returns {string} Die formatierte Tabelle als String
     */
    private formatArrayAsTable(data: string[][] | object[]): string {
        if (!data || data.length === 0) {
            return '';
        }

        let tableData: string[][];

        // Prüfen, ob es sich um ein Array von Objekten handelt und in string[][] umwandeln
        if (typeof data[0] === 'object' && !Array.isArray(data[0])) {
            const objectData = data as Record<string, any>[];
            let headers: string[] = Object.keys(objectData[0]);

            // Spalten entfernen, die nur null oder undefined enthalten
            headers = headers.filter(header => {
                return objectData.some(obj => obj[header] !== null && typeof obj[header] !== 'undefined');
            });

            // Spaltennamen für Bremen (hb) anpassen
            const displayHeaders = headers.map(h => (h === 'Pseudonym' && this.sessionvariablen.schule.land === 'hb') ? 'Schülercode' : h);

            tableData = [displayHeaders, ...objectData.map(obj => headers.map(header => {
                const value = obj[header];
                return (value === null || typeof value === 'undefined') ? '-' : String(value);
            }))]
        } else {
            tableData = data as string[][];
        }

        // Maximale Breite jeder Spalte berechnen
        const columnWidths: number[] = tableData[0].map((_, colIndex) => {
            return Math.max(...tableData.map(row => (row[colIndex] || '').length));
        });

        // Tabelle als String mit passenden Abständen formatieren
        return tableData.map(row => {
            return row
                .map((cell: string, colIndex: number) => {
                    if (cell === null || typeof cell === 'undefined' || cell === 'null') {
                        return '-'.padEnd(columnWidths[colIndex], ' ');
                    }
                    return cell.padEnd(columnWidths[colIndex], ' ');
                })
                .join('  '); // Zwei Leerzeichen als Trennung
        }).join('\n');
    }

    /**
     * @description Extrahiert Tabellendaten aus der Zwischenablage (ClipboardEvent)
     * und konvertiert sie in ein zweidimensionales Array (string[][])
     * @param {ClipboardEvent} evt - Das ClipboardEvent-Objekt
     * @returns {Promise<string[][] | null>} Ein zweidimensionales Array mit den Tabellendaten oder null bei einem Fehler
     */
    private async clipboardToArray(evt: ClipboardEvent): Promise<string[][] | null> {
        try {
            evt.stopPropagation();
            evt.preventDefault();

            // Clipboard extrahieren und normalisieren
            // @ts-ignore
            let clipboardData = evt.clipboardData || window.clipboardData;
            let clipboardText = clipboardData.getData('text/plain');
            let normalizedText = Utilities.normalizeString(clipboardText);

            // Text in Zeilen und Spalten (via Tabulator) aufteilen
            let clipboardTable: string[][] = normalizedText.split('\n')
                .map((row: string): string[] => {
                    return row.trim()
                        .split('\t')
                        .map((cell: string): string => {
                            let cellNormalized = cell.trim();
                            return (cellNormalized.length > 0) ? cellNormalized : null;
                        }) ?? []
                });

            if (clipboardTable.length === 0)
                return null;

            // Prüfen ob es sich um eine einzelne Zelle handelt
            if (clipboardTable.length === 1 && clipboardTable[0].length === 1)
                return null; // normales Paste ohne Zeilenumbrüche

            return clipboardTable;
        } catch (e) {
            Utilities.errorToSentry(e);

            return null;
        }
    }

    private async reformatClipboardData(e: ClipboardEvent): Promise<boolean> {
        let tsv: string[][] = await this.clipboardToArray(e);
        if (tsv === null) {
            let hinweisModal = new Modal(this);
            await hinweisModal.show({
                title: `Importierte Tabelle fehlerhaft`,
                body: `
                    <p>
                        Beim eingefügten Text handelt es sicht nicht um eine Tabelle.<br/>
                        Markieren und kopieren Sie Ihre Schülerliste aus einer Tabellenkalkulationssoftware (wie Excel oder LibreOffice Calc)
                        und fügen Sie die Tabelle erneut hier ein.
                    </p>`,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 500,
                cancelText: 'OK'
            });
            return false;
        }

        // Spaltenköpfe in erster Zeile erkennen und normalisieren
        let normalisiert: IImportDaten[];

        // Daten aus TSV auslesen transformieren
        normalisiert = this.datentransformation(tsv);

        // Zusammenfassung der importierten Daten erstellen
        let nSchueler: number = normalisiert.length;
        let nKlarname: number = normalisiert.filter(s => s.Klarname !== null).length;
        let nPseudonym: number = normalisiert.filter(s => s.Pseudonym !== null).length;
        let nGeschlecht: number = normalisiert.filter(s => s.Geschlecht !== null).length;
        let nMitStammklasse: number = normalisiert.filter(s => s.Stammklasse !== null).length;
        let nStammklassen: number = new Array(...new Set(normalisiert.filter(s => s.Stammklasse !== null).map(s => s.Stammklasse))).length;
        let nF: number = new Array(...new Set(normalisiert.filter(s => s.F !== null).map(s => s.F))).length;
        let nS: number = new Array(...new Set(normalisiert.filter(s => s.S !== null).map(s => s.S))).length;

        if (nSchueler < 1 || nKlarname + nGeschlecht + nMitStammklasse < 1) {
            let hinweisModal = new Modal(this);
            await hinweisModal.show({
                title: `Keine erwarteten Spaltenköpfe gefunden`,
                body: `
                    <p>
                        Achten Sie beim Kopieren Ihrer Schülerliste darauf, dass die <b>erste Zeile Spaltenköpfe</b> enthält.<br/>
                        Die erwarteten Spaltenbezeichnungen können Sie der Liste oben entnehmen.
                    </p>
                `,
                hasSubmit: false,
                hasCancel: true,
                hasBestaetigung: false,
                width: 500,
                cancelText: 'OK'
            });
            return;
        }

        // Importzusammenfassung im DOM anzeigen
        document.querySelector(`#datenimport-details`).innerHTML = `
            <b>Importzusammenfassung:</b>
            <ul>
                ${(nSchueler > 0) ? `<li>${nSchueler} SCHÜLER</li>` : ``}
                ${(nStammklassen > 0) ? `<li>${nStammklassen} STAMMKLASSEN</li>` : ``}
                ${(nKlarname > 0) ? `<li>Klarname (${nKlarname}/${nSchueler})</li>` : ``}
                ${(nPseudonym > 0) ? `<li>${(this.sessionvariablen.schule.land === 'hb') ? `Schülercode` : `Pseudonym`} (${nPseudonym}/${nSchueler})</li>` : ``}
                ${(nGeschlecht > 0) ? `<li>Geschlecht (${nGeschlecht}/${nSchueler})</li>` : ``}
                ${(nMitStammklasse > 0) ? `<li>zugewiesene Stammklasse (${nMitStammklasse}/${nSchueler})</li>` : ``}
                ${(nF > 0) ? `<li>Schüler mit Förderbedarf (${nF}/${nSchueler})</li>` : ``}
                ${(nS > 0) ? `<li>Schüler mit unzur. Sprachbeherrschung (${nS}/${nSchueler})</li>` : ``}
            </ul>
        `;
        (e.target as HTMLTextAreaElement).value = this.formatArrayAsTable(normalisiert);
        (document.querySelector(`#datenimport-importieren-btn`) as HTMLButtonElement).disabled = false;

        this.importDaten = normalisiert;
    }

    private datentransformation(tsv: string[][]): IImportDaten[] {
        let normalisiert: IImportDaten[] = [];

        // Spaltenköpfe normalisieren
        tsv[0] = tsv[0].map(s => s
            ?.toLowerCase()
            ?.replace(/\s+/gim, '')
            ?.trim()
        );

        if (this.sessionvariablen.schule.land === 'hb')
            this.normalisierungBremen(tsv);

        for (let y = 1; y < tsv.length; y++) {
            normalisiert[y - 1] = {
                Klarname: null,
                Pseudonym: null,
                Geschlecht: null,
                Stammklasse: null,
                F: null,
                S: null
            };

            // Spalte 'Klarname' suchen und zuweisen
            if (tsv[0].includes('klarname')) {
                normalisiert[y - 1].Klarname = tsv[y][tsv[0].indexOf('klarname')]?.substring(0, 19) ?? null;
            } else if (tsv[0].includes('name')) {
                normalisiert[y - 1].Klarname = tsv[y][tsv[0].indexOf('name')]?.substring(0, 19) ?? null;
            } else if (tsv[0].includes('vorname') && tsv[0].includes('nachname')) {
                normalisiert[y - 1].Klarname = `${tsv[y][tsv[0].indexOf('vorname')] ?? ''} ${tsv[y][tsv[0].indexOf('nachname')] ?? ''}`.substring(0, 19).trim();
                if (normalisiert[y - 1].Klarname?.trim().length === 0)
                    normalisiert[y - 1].Klarname = null;
            } else if (tsv[0].includes('schuelerrufname') && tsv[0].includes('schuelernachname')) {
                normalisiert[y - 1].Klarname = `${tsv[y][tsv[0].indexOf('schuelerrufname')] ?? ''} ${tsv[y][tsv[0].indexOf('schuelernachname')] ?? ''}`.substring(0, 19).trim();
                if (normalisiert[y - 1].Klarname?.trim().length === 0)
                    normalisiert[y - 1].Klarname = null;
            }

            // Spalte 'Geschlecht' suchen und normalisieren
            if (tsv[0].includes('geschlecht')) {
                normalisiert[y - 1].Geschlecht = tsv[y][tsv[0].indexOf('geschlecht')]
                    ?.trim()
                    ?.toLowerCase()
                    ?.replace('/^(m(ä|ae)nnlich|junge|mann|1)$/gi', 'm')
                    ?.replace('/^(weiblich|m(ä|ae)dchen|frau|2)$/gi', 'w')
                    ?.replace('/^(divers|nicht binär|nicht binaer|neutrum|3)$/gi', 'd') ?? null;
                if (['m', 'w', 'd'].includes(normalisiert[y - 1].Geschlecht) === false)
                    normalisiert[y - 1].Geschlecht = null;
            } else if (tsv[0].includes('schuelergeschlecht')) {
                normalisiert[y - 1].Geschlecht = tsv[y][tsv[0].indexOf('schuelergeschlecht')]
                    ?.trim()
                    ?.toLowerCase()
                    ?.replace('/^(m(ä|ae)nnlich|junge|mann|1)$/gi', 'm')
                    ?.replace('/^(weiblich|m(ä|ae)dchen|frau|2)$/gi', 'w')
                    ?.replace('/^(divers|nicht binär|nicht binaer|neutrum|3)$/gi', 'd') ?? null;
                if (['m', 'w', 'd'].includes(normalisiert[y - 1].Geschlecht) === false)
                    normalisiert[y - 1].Geschlecht = null;
            }

            // Spalte 'Stammklasse' suchen und zuweisen
            if (tsv[0].includes('klasse')) {
                normalisiert[y - 1].Stammklasse = tsv[y][tsv[0].indexOf('klasse')]?.trim() ?? null;
            } else if (tsv[0].includes('stammklasse')) {
                normalisiert[y - 1].Stammklasse = tsv[y][tsv[0].indexOf('stammklasse')]?.trim() ?? null;
            } else if (tsv[0].includes('gruppebezeichnung')) {
                normalisiert[y - 1].Stammklasse = tsv[y][tsv[0].indexOf('gruppebezeichnung')]?.trim() ?? null;
            }

            // Klassenbezeichnung verlängern, wenn nur ein Zeichen - z.B. "8" -> "Klasse 8"
            if (normalisiert[y - 1].Stammklasse !== null)
                if (normalisiert[y - 1].Stammklasse.length === 1)
                    normalisiert[y - 1].Stammklasse = `Klasse ${normalisiert[y - 1].Stammklasse}`;

            // Spalte 'Foerderbedarf' suchen und zuweisen
            if (tsv[0].includes('foerderbedarf')) {
                normalisiert[y - 1].F = ((tsv[y][tsv[0].indexOf('foerderbedarf')]?.trim() ?? '').length > 0) ? 'x' : null;
            } else if (tsv[0].includes('f')) {
                normalisiert[y - 1].F = ((tsv[y][tsv[0].indexOf('f')]?.trim() ?? '').length > 0) ? 'x' : null;
            }

            // Spalte 'unzureichende Sprachbeherrschung' suchen und zuweisen
            if (tsv[0].includes('unzureichendesprachbeherrschung')) {
                normalisiert[y - 1].S = ((tsv[y][tsv[0].indexOf('unzureichendesprachbeherrschung')]?.trim() ?? '').length > 0) ? 'x' : null;
            } else if (tsv[0].includes('s')) {
                normalisiert[y - 1].S = ((tsv[y][tsv[0].indexOf('s')]?.trim() ?? '').length > 0) ? 'x' : null;
            }

            // Spalte 'Pseudonym HB' suchen und zuweisen
            if (this.sessionvariablen.schule.land === 'hb') {
                if (tsv[0].includes('pseudonym')) {
                    normalisiert[y - 1].Pseudonym = tsv[y][tsv[0].indexOf('pseudonym')]?.substring(0, 7).trim() ?? null;
                }
            }
        }

        return normalisiert;
    }

    /**
     * Normalisiert die Bremer Schülerlisten vor dem generischen Transformationsprozess.
     * @param {string[][]} tsv - Zweidimensionaler Datenarray
     * @returns void
     */
    private normalisierungBremen(tsv: string[][]): void {
        // überflüssige Zeilen entfernen
        for (let y = tsv.length - 1; y >= 0; y--) {
            if (
                /^(gedrucktam)$/i.test(tsv[y][0]) ||
                /^(seite).+(von).+$/i.test(tsv[y][0])
            ) {
                tsv.splice(y, 1);
            }
        }

        // Stammklassen in Zeilen kopieren
        for (let y = 1; y < tsv.length; y++) {
            if (/^(Schueler)$/.test(tsv[y][0])) {
                let stammklasse = tsv[y - 1][0];
                for (let y2 = y + 1; y2 < tsv.length; y2++) {
                    if (/^(\d{1,3})$/.test(tsv[y2][0])) {
                        tsv[y2].push(stammklasse);
                    } else {
                        break;
                    }
                }
            }
        }

        // Spaltenkopf-Zeilen entfernen
        for (let y = tsv.length - 1; y >= 0; y--) {
            if (
                /^(Schueler)$/.test(tsv[y][0]) ||
                /^(Klassenstufe)$/.test(tsv[y][1]) ||
                /(Klassenlehrer)/.test(tsv[y][3])
            ) {
                tsv.splice(y, 1);
            }
        }

        tsv.unshift(['schuelernummer', 'vorname', 'nachname', 'pseudonym', 'foerderbedarf', 'geschlecht', 'stammklasse']);
    }

    /**
     * @description Übermittelt die importierten und normalisierten Schüler- und Gruppendaten an die API,
     * um sie in der Datenbank zu speichern
     * @param {IImportDaten[]} importDaten - Das Array mit den zu importierenden Daten
     * @returns {Promise<boolean>} True bei Erfolg, andernfalls false
     */
    private async datenUebermitteln(importDaten: IImportDaten[]): Promise<boolean> {
        if (importDaten === null)
            return false;

        let newGruppenArr: IGruppenDB[] = [];
        let newSchuelerArr: ISchuelerDB[] = [];
        let newGruppenZuweisungArr: ISchueler_gruppenDB[] = [];
        let klarnamenArr: { nr: number; klarname: string; }[] = [];


        // Neue Stammklassen aus den Importdaten erstellen
        for (const s of importDaten) {
            if (s.Stammklasse === null)
                continue;

            if (newGruppenArr.filter(g => g.bezeichnung === s.Stammklasse?.trim()).length === 0) {
                let thIdMathematik: number = null;
                let isCbtMathematik: number = 0;
                let thIdDeutsch: number = null;
                let isCbtDeutsch: number = 0;
                let thIdEnglisch: number = null;
                let isCbtEnglisch: number = 0;
                let thIdFranzoesisch: number = null;
                let isCbtFranzoesisch: number = 0;

                isCbtMathematik = this.schulstatus.is_cbt_schule_mathematik ?? 0;
                isCbtDeutsch = this.schulstatus.is_cbt_schule_deutsch ?? 0;
                isCbtEnglisch = this.schulstatus.is_cbt_schule_englisch ?? 0;
                isCbtFranzoesisch = this.schulstatus.is_cbt_schule_franzoesisch ?? 0;

                // In Bremen ist CBT immer voreingestellt
                if (this.sessionvariablen.schule.land === 'hb') {
                    isCbtMathematik = 1;
                    isCbtDeutsch = 1;
                    isCbtEnglisch = 1;
                    isCbtFranzoesisch = 1;
                }

                if (this.sessionvariablen.projekt.weiche === 'vera5') {
                    const testheftMathematik = this.testheftVarianten.find((t) => t.fach === 'ma');
                    const testheftDeutsch = this.testheftVarianten.find((t) => t.fach === 'de');

                    thIdMathematik = testheftMathematik?.testheft_id ?? null;
                    isCbtMathematik = (testheftMathematik?.cbt === true) ? 1 : 0;
                    thIdDeutsch = testheftDeutsch?.testheft_id ?? null;
                    isCbtDeutsch = (testheftDeutsch?.cbt === true) ? 1 : 0;
                }

                // Nur in Gymnasien kann sicher das schwere Testheft vorausgewählt werden
                if (this.sessionvariablen.projekt.weiche !== 'vera5' && this.sessionvariablen.schule.schulform === 'GY') {
                    // Mathematik
                    if (this.sessionvariablen.schule.teilnahme_mathematik === 1) {
                        if (this.sessionvariablen.schule.land === 'nw' && this.schulstatus.is_cbt_schule_mathematik === 0) {
                            thIdMathematik = this.testheftliste.filter((t) => {
                                return t.fach === 'ma' && t.schwierigkeit === 3 && t.is_cbt === 0
                            })[0]?.testheft_id ?? null
                        }

                        if (this.sessionvariablen.schule.land === 'hb') {
                            thIdMathematik = this.testheftliste.filter((t) => {
                                return t.fach === 'ma' && t.schwierigkeit === 3 && t.is_cbt === 1
                            })[0]?.testheft_id ?? null
                        }
                    }

                    // Deutsch
                    if (this.sessionvariablen.schule.teilnahme_deutsch === 1) {
                        if (this.sessionvariablen.schule.land === 'nw' && this.schulstatus.is_cbt_schule_deutsch === 0) {
                            thIdDeutsch = this.testheftliste.filter((t) => {
                                return t.fach === 'de' && t.schwierigkeit === 3 && t.is_cbt === 0
                            })[0]?.testheft_id ?? null
                        }

                        if (this.sessionvariablen.schule.land === 'hb') {
                            thIdDeutsch = this.testheftliste.filter((t) => {
                                return t.fach === 'de' && t.schwierigkeit === 3 && t.is_cbt === 1
                            })[0]?.testheft_id ?? null
                        }
                    }

                    // Englisch
                    if (this.sessionvariablen.schule.teilnahme_englisch === 1) {
                        if (this.sessionvariablen.schule.land === 'nw' && this.schulstatus.is_cbt_schule_englisch === 0) {
                            thIdEnglisch = this.testheftliste.filter((t) => {
                                return t.fach === 'en' && t.schwierigkeit === 3 && t.is_cbt === 0
                            })[0]?.testheft_id ?? null
                        }

                        if (this.sessionvariablen.schule.land === 'hb') {
                            thIdEnglisch = this.testheftliste.filter((t) => {
                                return t.fach === 'en' && t.schwierigkeit === 3 && t.is_cbt === 1
                            })[0]?.testheft_id ?? null
                        }
                    }
                }

                newGruppenArr.push({
                    typ: 'STAMMKLASSE',
                    bezeichnung: s.Stammklasse,
                    testheft_mathematik: thIdMathematik,
                    testheft_deutsch: thIdDeutsch,
                    testheft_englisch: thIdEnglisch,
                    testheft_franzoesisch: thIdFranzoesisch,
                    is_cbt_mathematik: (thIdMathematik !== null) ? isCbtMathematik : 0,
                    is_cbt_deutsch: (thIdDeutsch !== null) ? isCbtDeutsch : 0,
                    is_cbt_englisch: (thIdEnglisch !== null) ? isCbtEnglisch : 0,
                    is_cbt_franzoesisch: (thIdFranzoesisch !== null) ? isCbtFranzoesisch : 0
                });
            }
        }

        // Neue Schüler und Klarnamen erstellen
        for (const s of importDaten) {
            newSchuelerArr.push({
                geschlecht: (s.Geschlecht === 'm') ? 1 : (s.Geschlecht === 'w') ? 2 : (s.Geschlecht === 'd') ? 3 : null,
                is_f: (s.F === 'x') ? 1 : 0,
                is_s: (s.S === 'x') ? 1 : 0,
                pseudonym_hb: s.Pseudonym ?? null,
            });

            klarnamenArr.push({
                nr: newSchuelerArr.length,
                klarname: s.Klarname
            });
        }

        // Daten an den Server übertragen
        const api = VeraApi.getInstance(this);
        let addedSchuelerArr: ISchuelerDB[] = await api.postSchueler(newSchuelerArr);
        if (addedSchuelerArr.length === 0)
            return false;

        let addedGruppenArr: IGruppenDB[] = await api.postGruppe(newGruppenArr);
        if (addedGruppenArr.length === 0)
            return false;

        let isKlarnamenAbgespeichert = await this.tresor.datenimportKlarnamen(klarnamenArr);
        if (isKlarnamenAbgespeichert === false)
            return false;

        // Schüler den neu erstellten Stammklassen zuweisen
        for (let i = 0; i < importDaten.length; i++) {
            if (importDaten[i].Stammklasse === null)
                continue;

            newGruppenZuweisungArr.push({
                schueler_id: addedSchuelerArr[i]?.schueler_id ?? null,
                gruppe_id: addedGruppenArr.filter(g => g.bezeichnung === importDaten[i].Stammklasse?.trim())[0]?.gruppe_id ?? null
            });
        }

        let zuweisungArr: ISchueler_gruppenDB[] = await api.postSchuelerGruppenZuweisungen(newGruppenZuweisungArr.filter(z => z.gruppe_id !== null && z.schueler_id !== null));
        if (zuweisungArr.length === 0)
            return false;

        return true;
    }
}
